import { create } from 'zustand'
import type { Campaign, FlashDeal } from '@/modules/promo/model/types'
import { promoApi } from '@/modules/promo-api'

function cleanPayload<T extends Record<string, any>>(obj: T): Partial<T> {
  const { id, discountPct, soldPct, createdAt, updatedAt, ...r } = obj as any
  if (r.endAt === null || r.endAt === '') delete r.endAt
  return r
}

interface PromoState {
  campaigns: Campaign[]
  deals: FlashDeal[]
  loaded: boolean
  loading: boolean
  error: string | null
  fetchAll: () => Promise<void>
  addCampaign: (c: Omit<Campaign, 'id'>) => Promise<void>
  updateCampaign: (id: string, patch: Partial<Campaign>) => Promise<void>
  removeCampaign: (id: string) => Promise<void>
  getCampaignById: (id: string) => Campaign | undefined
  addDeal: (d: Omit<FlashDeal, 'id'>) => Promise<void>
  updateDeal: (id: string, patch: Partial<FlashDeal>) => Promise<void>
  removeDeal: (id: string) => Promise<void>
  getDealById: (id: string) => FlashDeal | undefined
  resetAll: () => Promise<void>
}

export const useAdminPromoStore = create<PromoState>()((set, get) => ({
  campaigns: [],
  deals: [],
  loaded: false,
  loading: false,
  error: null,

  fetchAll: async () => {
    if (get().loading) return
    set({ loading: true, error: null })
    try {
      const [cs, ds] = await Promise.all([promoApi.listCampaigns(), promoApi.listFlashDeals()])
      set({
        campaigns: Array.isArray(cs) ? cs : (((cs as any)?.items ?? []) as Campaign[]),
        deals: Array.isArray(ds) ? ds : (((ds as any)?.items ?? []) as FlashDeal[]),
        loaded: true,
        loading: false,
      })
    } catch (err: any) {
      console.warn('[promo] fetch failed:', err?.message)
      set({ campaigns: [], deals: [], loaded: true, loading: false, error: err?.message ?? 'Không tải được khuyến mãi' })
    }
  },

  addCampaign: async (c) => {
    const created = await promoApi.adminCreateCampaign(cleanPayload(c) as any)
    set({ campaigns: [created, ...get().campaigns] })
  },
  updateCampaign: async (id, patch) => {
    const updated = await promoApi.adminUpdateCampaign(id, cleanPayload(patch))
    set({ campaigns: get().campaigns.map((x) => (x.id === id ? updated : x)) })
  },
  removeCampaign: async (id) => {
    await promoApi.adminDeleteCampaign(id)
    set({ campaigns: get().campaigns.filter((x) => x.id !== id) })
  },
  getCampaignById: (id) => get().campaigns.find((x) => x.id === id),

  addDeal: async (d) => {
    const created = await promoApi.adminCreateFlashDeal(cleanPayload(d) as any)
    set({ deals: [created, ...get().deals] })
  },
  updateDeal: async (id, patch) => {
    const updated = await promoApi.adminUpdateFlashDeal(id, cleanPayload(patch))
    set({ deals: get().deals.map((x) => (x.id === id ? updated : x)) })
  },
  removeDeal: async (id) => {
    await promoApi.adminDeleteFlashDeal(id)
    set({ deals: get().deals.filter((x) => x.id !== id) })
  },
  getDealById: (id) => get().deals.find((x) => x.id === id),

  resetAll: async () => { await get().fetchAll() },
}))
